import React, { useState, useEffect } from 'react';
import { Auth } from 'aws-amplify';
import QRCode from 'react-qr-code';
import '../css/mfaSetup.css';

// Module-level cache for the setup promise to handle StrictMode and rapid remounts
let setupPromise = null;

const MfaSetup = ({ onSuccess, onCancel, onError }) => {
  const [secret, setSecret] = useState('');
  const [qrUrl, setQrUrl] = useState('');
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(true);
  const [uiError, setUiError] = useState('');
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let isMounted = true;

    const performSetup = async () => {
      try {
        // If there's no ongoing setup, start one
        if (!setupPromise) {
          setupPromise = (async () => {
            const user = await Auth.currentAuthenticatedUser();
            return await Auth.setupTOTP(user);
          })();
        }

        const totpCode = await setupPromise;

        if (!isMounted) return;

        const user = await Auth.currentAuthenticatedUser();
        const issuer = 'eKukka';
        const account = encodeURIComponent(user.attributes.email);
        const url = `otpauth://totp/${issuer}:${account}?secret=${totpCode}&issuer=${issuer}`;

        setSecret(totpCode);
        setQrUrl(url);
        setLoading(false);
      } catch (err) {
        // Clear the promise so we can try again on next mount if it failed
        setupPromise = null;

        if (!isMounted) return;
        
        console.error('MFA setup error:', err);
        
        if (err.code === 'ConcurrentModificationException' || (err.message && err.message.includes('Only one request'))) {
          setUiError('Toinen MFA-piyntö on jo käynnissä. Odota hetki tai päivitä sivu.');
        } else {
          setUiError('MFA-asetusten luonti epäonnistui.');
        }
        setLoading(false);
      }
    };

    performSetup();

    return () => {
      isMounted = false;
      // Note: We don't clear setupPromise here because we want the 
      // next mount (in StrictMode) to be able to use the same promise.
    };
  }, []);

  // -----------------------------------------------------------------
  // 2. Verify the 6-digit code
  // -----------------------------------------------------------------
  const verifyCode = async () => {
    setUiError('');                       // clear previous UI error

    if (code.length !== 6) {
      setUiError('Anna 6-numeroinen koodi.');
      return;
    }

    try {
      const user = await Auth.currentAuthenticatedUser();
      await Auth.verifyTotpToken(user, code);
      await Auth.setPreferredMFA(user, 'TOTP');

      // Clear the cache promise on success so future setups (if any) are fresh
      setupPromise = null;

      onSuccess?.();
    } catch (err) {
      setUiError('Virheellinen koodi.');
    }
  };

  // -----------------------------------------------------------------
  // Copy secret to clipboard
  // -----------------------------------------------------------------
  const copySecret = () => {
    navigator.clipboard.writeText(secret)
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      })
      .catch(err => {
        console.error('Failed to copy secret:', err);
        setUiError('Salaisen avaimen kopiointi epäonnistui.');
      });
  };

  // Truncate secret for display (show first 8 + ... + last 8 chars)
  const truncatedSecret = secret ? `${secret.slice(0, 8)}...${secret.slice(-8)}` : '';

  // -----------------------------------------------------------------
  // 3. Render
  // -----------------------------------------------------------------
  return (
    <div
      style={{
        margin: '15px 0',
        padding: '15px',
        background: '#f9f9f9',
        borderRadius: '6px',
        fontSize: '14px',
      }}
    >
      <p>
        <strong>
          Skannaa QR-koodi käyttämällä todennussovellus kuten Google tai Microsoft
          authenticator, ja syötä sovelluksessa näkyvä koodi.
        </strong>
      </p>



      {/* ---------- LOADING ---------- */}
      {loading ? (
        <p>Ladataan QR-koodia…</p>
      ) : qrUrl ? (
        <>
          <div style={{ textAlign: 'center', margin: '10px 0' }}>
            <QRCode value={qrUrl} size={160} />
          </div>

          <p>Tai syötä koodi manuaalisesti:</p>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '8px 0',
            }}
          >
            <code
              style={{
                padding: '8px 12px 8px 8px',
                background: '#eee',
                fontFamily: 'monospace',
                border: '1px solid #ccc',
                borderRight: 'none',
                borderRadius: '4px 0 0 4px',
                textAlign: 'left',
              }}
            >
              {truncatedSecret}
            </code>
            <button
              onClick={copySecret}
              style={{
                padding: '8px 12px',
                border: '1px solid #ccc',
                borderLeft: 'none',
                borderRadius: '0 4px 4px 0',
                background: '#ddd',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
              title={copied ? 'Kopioitu!' : 'Kopioi salainen avain'}
            >
              {copied ? (
                <svg width="16" height="16" viewBox="0 0 24 24" fill="#4caf50">
                  <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
                </svg>
              ) : (
                <svg width="16" height="16" viewBox="0 0 24 24" fill="#333">
                  <path d="M16 1H4c-1.1 0-2 .9-2 2v14h2V3h12V1zm3 4H8c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h11c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm0 16H8V7h11v14z" />
                </svg>
              )}
            </button>
          </div>

          {/* ---------- UI ERROR ---------- */}
          {uiError && (
            <div
              className='general_error_informer'
            >
              {uiError}
            </div>
          )}


          <div
            className='qrCodeContainer'
          >
            <input
              type="text"
              value={code}
              onChange={(e) => {
                const val = e.target.value.replace(/\D/g, '').slice(0, 6);
                setCode(val);
              }}
              placeholder="123456"
              maxLength="6"
              style={{
                width: '100px',
                padding: '8px',
                textAlign: 'center',
                fontSize: '18px',
                fontFamily: 'monospace',
                border: '1px solid #ccc',
                borderRadius: '4px',
              }}
            />
            <button
              onClick={verifyCode}
              className="ekukka-btn ekukka-btn-primary"
              style={{ padding: '8px 12px' }}
            >
              Vahvista
            </button>
            <button
              onClick={onCancel}
              className="ekukka-btn ekukka-btn-secondary"
              style={{ padding: '8px 12px' }}
            >
              Peruuta
            </button>
          </div>
        </>
      ) : (
        <p style={{ color: '#d32f2f' }}>QR-koodin luonti epäonnistui.</p>
      )}
    </div>
  );
};

export default MfaSetup;